import { z } from 'zod';

/**
 * What the extractor returns for one vendor document or email.
 *
 * Ported from everplan's `shared/schemas/quote-import.ts`, dropping the fields
 * that have no consumer here: the vendor is already known by the time a quote
 * arrives, so `vendorName`, `vendorContactInfo` and `suggestedCategory` would
 * be extracted and discarded.
 *
 * Money is integer cents throughout. The prompt's central rule is "all
 * monetary amounts in CENTS", every function downstream assumes it, and mixing
 * cents with the `numeric(12,2)` on `inquiries.quoteAmount` across a boundary
 * is how rounding bugs get in. The one conversion lives in `quote-resolve.ts`.
 *
 * None of the money fields (`unitCost`, `amount`, `statedTotal`) are
 * `.nonnegative()`. Everplan's prompt forbids modelling a discount as a
 * charge — it belongs in the package description instead, so a negative
 * should never arise by design. And the extractor fails closed: a value
 * that fails validation discards the whole extraction, so rejecting one
 * stray negative would lose an otherwise-good three-package quote. Storing
 * an odd sign we can see is cheaper than that.
 */

/**
 * Integer cents, bounded to what the column can physically hold.
 *
 * This is a column-width guard, not a business rule. Every money field below
 * lands in a Postgres `integer` (`int4`), which tops out at 2,147,483,647 —
 * and a value above that validates happily here and then aborts the whole
 * insert transaction in `quote-persist.ts`. That failure mode is the
 * expensive one: it is raised at write time, far from the model that produced
 * the number, and it takes the entire tree down with it.
 *
 * $20M in cents sits safely inside `int4` and is orders of magnitude beyond
 * any real wedding quote, so the bound only ever fires on a model that
 * concatenated a digit run or slipped a decimal. It matters more than it
 * looks: the anchor guard in `quote-extract.ts` grounds `statedTotal` against
 * the vendor's own text and nothing else, so `unitCost`, charge `amount` and
 * add-on `unitCost` reach the insert entirely unchecked against the message.
 * This is the only thing standing between a hallucinated digit and a failed
 * transaction.
 *
 * Applied to `quantity` as well as to the money fields: it is the same column
 * type with the same ceiling, and the fact that it counts chairs rather than
 * cents changes nothing about what Postgres will accept.
 *
 * Symmetric because the reason is symmetric — `int4`'s floor is
 * -2,147,483,648, and a large negative aborts the same insert as a large
 * positive. It is deliberately *not* `.nonnegative()`: that decision was
 * taken in Task 0 and stands (a stray negative sign we can see beats losing
 * an otherwise-good three-package quote). If these columns ever become
 * `bigint`, this bound moves with them.
 */
const INT4_GUARD = 2_000_000_000;
const cents = () => z.number().int().min(-INT4_GUARD).max(INT4_GUARD);
/** Same guard, same reason — `quote_line_items.quantity` and
 *  `quote_add_ons.quantity` are `integer` too, and a model that fumbles a
 *  headcount aborts the insert exactly as a fumbled price does. */
const count = () => z.number().int().min(-INT4_GUARD).max(INT4_GUARD);

export const parsedLineItemSchema = z.object({
  /** LLM-assigned, stable within one response ("L1", "L2"). Phase 3 uses these
   *  to wire percentage charges to the items they apply to; nothing reads them
   *  yet, and they are never shown to a planner. */
  id: z.string().optional(),
  name: z.string().min(1),
  description: z.string().nullable().default(null),
  /** Null means the document showed a per-unit price with no confirmed
   *  count ("$25 per person", a menu option with no headcount) or no
   *  quantity concept at all (a flat fee, a percentage service charge).
   *  The extraction prompt is explicit that a count is only ever set when
   *  the document states one outright ("120 guests", "Qty: 5") — when in
   *  doubt it stays null rather than guess. `packageTotal` in
   *  `quote-totals.ts` folds a null to one unit when it has to produce a
   *  number at all; that fold is a guess this field's null was written to
   *  avoid, so anything reading this value for a real total should also
   *  consult `hasEstimatedQuantity`. */
  quantity: count().nullable().default(null),
  unitCost: cents(),
  section: z.string().nullable().default(null),
});

export const parsedChargeSchema = z.object({
  id: z.string().optional(),
  type: z.enum(['delivery', 'labor', 'tax', 'other']),
  description: z.string().nullable().default(null),
  amount: cents(),
});

/** Same shape as a line item today, but a distinct concept: an add-on is a
 *  rate-card entry the customer has not committed to, where a line item is a
 *  charge they have. The extractor prompt tells them apart by whether the
 *  document shows an extended row total. This alias is expected to diverge
 *  from `parsedLineItemSchema` once add-ons need fields line items don't. */
export const parsedAddOnSchema = parsedLineItemSchema;

export const parsedPackageSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().default(null),
  lineItems: z.array(parsedLineItemSchema),
  additionalCharges: z.array(parsedChargeSchema).default([]),
  /** The grand total for this package as printed. Never computed — a summed
   *  total that disagrees with the printed one is a warning, not a correction. */
  statedTotal: cents().nullable().default(null),
});

/**
 * Consumed by post-processing (Task 2) and then stripped — it never reaches
 * the persisted shape.
 *
 * Deliberately unbounded, unlike the money fields above: these numbers reach a
 * column only by being *copied* into one — injected as a charge `amount`, or
 * backfilled onto a single package's `statedTotal` — and post-processing runs
 * before validation, so the copy is what gets checked. Bounding here as well
 * would reject a document whose garbage total we were about to discard anyway.
 */
export const documentTotalsSchema = z.object({
  subtotal: z.number().int().nullable().default(null),
  tax: z.number().int().nullable().default(null),
  freight: z.number().int().nullable().default(null),
  total: z.number().int().nullable().default(null),
});

/**
 * The whole extraction.
 *
 * One asymmetry worth knowing before trusting this as a uniform gate:
 * `parseResult` in `quote-extract.ts` validates `packages` *per element* and
 * then hands the survivors here, so one malformed tier drops on its own and
 * the tiers beside it live. `addOns` and the top-level `additionalCharges`
 * have no such treatment — they go through this schema all-or-nothing, so a
 * single out-of-range add-on discards the entire quote, packages included.
 * That is the fail-closed side of the trade and it is accepted for now: the
 * alternative is a second per-element loop for a field that is empty on the
 * overwhelming majority of real messages.
 */
export const structuredQuoteSchema = z.object({
  packages: z.array(parsedPackageSchema),
  addOns: z.array(parsedAddOnSchema).default([]),
  additionalCharges: z.array(parsedChargeSchema).default([]),
  confidence: z.enum(['high', 'medium', 'low']),
  warnings: z.array(z.string()).default([]),
});

export type StructuredQuote = z.infer<typeof structuredQuoteSchema>;
export type ParsedPackage = z.infer<typeof parsedPackageSchema>;
export type ParsedLineItem = z.infer<typeof parsedLineItemSchema>;
export type ParsedCharge = z.infer<typeof parsedChargeSchema>;
export type DocumentTotals = z.infer<typeof documentTotalsSchema>;
